import { useDispatch, useSelector } from 'react-redux';

import {
	createConfiguration,
	peekSensorConnection,
	peekSensorAsync,
	updateConfiguration,
	pollPeekAsyncResultsStop,
	pollSensorTroubleshootingStop,
} from '@Actions';
import { SensorTestSchema } from '@Pages/shared/SensorTestSchema';
import { getValue, selectConfigurations } from '@Selectors';
import { useGlobalization } from '@Translations';
import { SensorConfigFormShape } from '@Types/configurations.types';
import { Features, error, useIsFeatureEnabled } from '@innovyze/stylovyze';
import { ObjectSchema } from 'yup';
import EditSensorSchema from '../pages/shared/SensorSchema';
import { useFormState } from './useFormState';
import useYupSchemaValidation, {
	ValidationReturn,
} from './useYupSchemaValidation';
import { useEffect, useState } from 'react';
import { FLAGS } from '@Types/flags';
import useFeatureFlagConfiguration from './useFeatureFlagConfigurations';

type SensorForm = SensorConfigFormShape;
type SensorFormStateReturn = [
	SensorForm,
	SensorForm,
	(newFormData: React.SetStateAction<SensorForm>) => void,
	(create?: boolean) => void,
	boolean,
	(state: boolean) => void,
	() => void,
	() => void,
];

const useSensorFormState = (
	configurationId?: string,
): SensorFormStateReturn => {
	const dispatch = useDispatch();
	const { t } = useGlobalization();

	const isNewSensorTestEnabled = !!useIsFeatureEnabled(
		FLAGS.NEW_SENSOR_SYSTEM_TESTS as keyof Features,
	);

	const { asyncTestsTimeoutMs } = useFeatureFlagConfiguration();

	const {
		systemConnections: { list: systemConnectionsList },
	} = useSelector(getValue);

	const initialValues: SensorForm = {
		dataSourceName: '',
		dataSourceType: 'ODBC',
		assetId: '',
		collectionInterval: '',
		dataSourceId: '',
		interpolationEnabled: '',
		interpolationMethod: '',
		location: '',
		sensorAlias: '',
		sensorChannelField: '',
		sensorDateField: '',
		sensorId: '',
		sensorIdField: '',
		sensorTable: '',
		sensorTimeField: '',
		sensorType: '',
		sensorUnitConversion: '',
		tagValue: '',
		tankCurveId: '',
		unit: '',
		tankType: 'cylindrical',
		tankOffset: '',
		tankDiameter: '',
		isPhysical: 'true',
	};

	const [
		editedForm,
		setEditedForm,
		formData,
		setFormData,
		formErrors,
		setFormErrors,
	] = useFormState<SensorForm>(initialValues);
	const [validate] = useYupSchemaValidation<SensorForm>();

	const [isValidated, setIsValidated] = useState(false);
	const [lastSensorId, setLastSensorId] = useState('');
	const { errorCategory, errorMsg } = useSelector(selectConfigurations);

	useEffect(() => {
		revalidateForm();
	}, [formData?.isPhysical]);

	useEffect(() => {
		if (errorCategory && errorMsg) revalidateForm();
	}, [errorCategory, errorMsg]);

	const handleTestSensorPollStop = () => {
		dispatch(pollPeekAsyncResultsStop());
		dispatch(pollSensorTroubleshootingStop());
	};

	useEffect(() => {
		return () => {
			handleTestSensorPollStop(); // * Teardown (component unmount)
		};
	}, []);

	function validateFormData(formDataParam: SensorForm, schema: ObjectSchema) {
		if (!isValidated) setIsValidated(true);
		const validationResults: ValidationReturn<SensorConfigFormShape> =
			validate(formDataParam, schema);

		// Duplication error only handled in virtual sensors for now
		if (
			errorMsg &&
			formData?.isPhysical === 'false' &&
			(!lastSensorId || formData?.sensorId === lastSensorId)
		) {
			validationResults.errors = {
				...validationResults.errors,
				sensorId: t(errorMsg),
			};

			setLastSensorId(formData?.sensorId ?? '');

			validationResults.hasErrors = true;
		}

		if (validationResults.hasErrors) {
			setFormErrors({ ...(validationResults.errors as SensorForm) });
			return false;
		}

		setFormErrors({} as SensorForm);
		return true;
	}

	function updateFormData(newFormData: React.SetStateAction<SensorForm>) {
		// performance issues
		setFormData(newFormData);
	}

	function createUpdateConfigurationFn(create = false) {
		if (validateFormData(formData, EditSensorSchema(t, formData))) {
			const dataSource = systemConnectionsList.find(
				({ SystemConnectionId }) =>
					SystemConnectionId === formData.dataSourceId,
			);

			if (dataSource) {
				formData.dataSourceName = dataSource.connectionName;
			}

			if (create === false)
				dispatch(
					updateConfiguration({
						configurationId: configurationId ?? '',
						payload: formData,
					}),
				);
			else dispatch(createConfiguration(formData));
		} else {
			dispatch(error('Form is not valid'));
		}
	}

	function peekSensorConnectionFn() {
		if (validateFormData(formData, SensorTestSchema(t))) {
			const request = {
				peekForm: formData,
				timeoutMs: asyncTestsTimeoutMs,
			};
			if (isNewSensorTestEnabled) {
				dispatch(peekSensorAsync(request));
			} else {
				dispatch(peekSensorConnection(request));
			}
		} else {
			dispatch(error('Form is not valid'));
		}
	}

	function setEditedFormState(state: boolean) {
		setEditedForm(state);
	}

	function revalidateForm() {
		if (
			isValidated &&
			!validateFormData(formData, EditSensorSchema(t, formData))
		) {
			dispatch(error('Form is not valid'));
		}
	}

	return [
		formData,
		formErrors,
		updateFormData,
		createUpdateConfigurationFn,
		editedForm,
		setEditedFormState,
		peekSensorConnectionFn,
		revalidateForm,
	];
};

export default useSensorFormState;
